import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';

// Main categories and their subcategories
const categories = [
    {
        label: 'Educational Support',
        value: 'educational',
        subcategories: [
            'Barefoot No More Project',
            'Lap Desk Project',
            'The Rialto Project',
            'The Cangro Project',
            'Project Dignity',
        ],
    },
    {
        label: 'Sport for Youth',
        value: 'sport',
        subcategories: [
            'Sporting Chance Programmes',
            'Soccer/Netball Team Project',
        ],
    },
    {
        label: 'Community Upliftment',
        value: 'community',
        subcategories: [
            'Memeza Project',
            'Sustainable Food Security Project',
        ],
    },
];

const getCurrentYear = () => new Date().getFullYear();
const getCurrentMonth = () => new Date().getMonth(); // 0-based

const years = [];
for (let y = 2020; y <= getCurrentYear(); y++) {
    years.push(y);
}
const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

const CsiProgrammesDataPage = () => {
    // State
    const [mainCategory, setMainCategory] = useState(categories[0].value);
    const [subCategory, setSubCategory] = useState(categories[0].subcategories[0]);
    const [year, setYear] = useState(getCurrentYear());
    const [month, setMonth] = useState(getCurrentMonth());
    const [count, setCount] = useState('');
    const [communitiesImpacted, setCommunitiesImpacted] = useState('');
    const [programmesCount, setProgrammesCount] = useState('');

    // Get subcategories for selected main category
    const subcategories = categories.find(c => c.value === mainCategory).subcategories;

    // When main category changes, reset subcategory
    const handleMainCategoryChange = (e) => {
        const newMain = e.target.value;
        setMainCategory(newMain);
        const newSub = categories.find(c => c.value === newMain).subcategories[0];
        setSubCategory(newSub);
    };

    // When year changes, reset month if needed
    const handleYearChange = (e) => {
        const selectedYear = parseInt(e.target.value, 10);
        setYear(selectedYear);
        // If selected year is current and month is in the future, reset month
        if (selectedYear === getCurrentYear() && month > getCurrentMonth()) {
            setMonth(getCurrentMonth());
        }
    };

    // Only show months up to current month if year is current
    const getAvailableMonths = () => {
        if (year === getCurrentYear()) {
            return months.slice(0, getCurrentMonth() + 1);
        }
        return months;
    };

    // Form display logic
    const showForm = !!mainCategory && !!subCategory;

    // Special case for Sporting Chance Programmes
    const isSportingChance = subCategory === 'Sporting Chance Programmes';
    const isSustainableFoodSecurity = subCategory === 'Sustainable Food Security Project';

    const getCategoryId = (mainCategory) => {
        if (mainCategory === 'educational') return 1;
        if (mainCategory === 'sport') return 2;
        if (mainCategory === 'community') return 3;
        return 0;
    };

    const getPeriod = () => {
        const monthStr = String(month + 1).padStart(2, '0');
        return parseInt(`${year}${monthStr}`, 10);
    };

    const handleSave = async () => {
        // Validation
        if (!count || Number(count) <= 0) {
            toast.error('Count is required and must be greater than 0.');
            return;
        }

        const requestBody = {
            projectName: subCategory,
            period: getPeriod(),
            count: Number(count),
            csiCategoryID: getCategoryId(mainCategory),
            programCount: isSportingChance && programmesCount ? Number(programmesCount) : null,
            communityCount: (isSportingChance || isSustainableFoodSecurity) && communitiesImpacted ? Number(communitiesImpacted) : null,
        };

        try {
            const response = await fetch('/api/marketing/save-project-data', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(requestBody),
            });
            const result = await response.json();
            if (result.success) {
                toast.success('Data saved successfully!');
            } else {
                toast.error(result.message || 'Failed to save data.');
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('An error occurred while saving.');
        }
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            // Reset fields before fetching
            setCount('');
            setCommunitiesImpacted('');
            setProgrammesCount('');

            const fetchData = async () => {
            const response = await fetch(
                `/api/marketing/get-csi-project-data?projectName=${encodeURIComponent(subCategory)}&period=${getPeriod()}`
            );
            const result = await response.json();
            if (result.found && result.data) {
                setCount(result.data.count ?? '');
                setCommunitiesImpacted(result.data.communityCount ?? '');
                setProgrammesCount(result.data.programCount ?? '');
            }
        };

        if (subCategory && year && month >= 0) {
            fetchData();
        }
        }, 0);
        return () => clearTimeout(timer);
    }, [subCategory, year, month]);

    return (
        <div className="csi-input">
            <h1>CSI Programmes Data Entry</h1>
            <div style={{ marginBottom: 16 }}>
                <label>
                    Main Category:&nbsp;
                    <select value={mainCategory} onChange={handleMainCategoryChange}>
                        {categories.map(cat => (
                            <option key={cat.value} value={cat.value}>{cat.label}</option>
                        ))}
                    </select>
                </label>
            </div>
            <div style={{ marginBottom: 16 }}>
                <label>
                    Sub Category:&nbsp;
                    <select
                        value={subCategory}
                        onChange={e => setSubCategory(e.target.value)}
                    >
                        {subcategories.map(sub => (
                            <option key={sub} value={sub}>{sub}</option>
                        ))}
                    </select>
                </label>
            </div>
            {showForm && (
                <form onSubmit={e => e.preventDefault()}>
                    <div style={{ marginBottom: 16 }}>
                        <label>
                            Year:&nbsp;
                            <select value={year} onChange={handleYearChange}>
                                {years.map(y => (
                                    <option key={y} value={y}>{y}</option>
                                ))}
                            </select>
                        </label>
                        &nbsp;&nbsp;
                        <label>
                            Month:&nbsp;
                            <select
                                value={month}
                                onChange={e => setMonth(Number(e.target.value))}
                            >
                                {getAvailableMonths().map((m, idx) => (
                                    <option key={m} value={idx}>{m}</option>
                                ))}
                            </select>
                        </label>
                    </div>
                    {isSportingChance ? (
                        <>
                            <div style={{ marginBottom: 16 }}>
                                <label>
                                    Count:&nbsp;
                                    <input
                                        type="number"
                                        min="0"
                                        value={count}
                                        onChange={e => setCount(e.target.value)}
                                    />
                                </label>
                            </div>
                            <div style={{ marginBottom: 16 }}>
                                <label>
                                    Communities Impacted:&nbsp;
                                    <input
                                        type="number"
                                        min="0"
                                        value={communitiesImpacted}
                                        onChange={e => setCommunitiesImpacted(e.target.value)}
                                    />
                                </label>
                            </div>
                            <div style={{ marginBottom: 16 }}>
                                <label>
                                    Programmes Count:&nbsp;
                                    <input
                                        type="number"
                                        min="0"
                                        value={programmesCount}
                                        onChange={e => setProgrammesCount(e.target.value)}
                                    />
                                </label>
                            </div>
                        </>
                    ) : (
                        <>
                            <div style={{ marginBottom: 16 }}>
                                <label>
                                    Count:&nbsp;
                                    <input
                                        type="number"
                                        min="0"
                                        value={count}
                                        onChange={e => setCount(e.target.value)}
                                    />
                                </label>
                            </div>
                            {isSustainableFoodSecurity && (
                                <div style={{ marginBottom: 16 }}>
                                    <label>
                                        Community Impacted:&nbsp;
                                        <input
                                            type="number"
                                            min="0"
                                            value={communitiesImpacted}
                                            onChange={e => setCommunitiesImpacted(e.target.value)}
                                        />
                                    </label>
                                </div>
                            )}
                        </>
                    )}
                    <button type="button" onClick={handleSave}>Save</button>
                </form>
            )}
</div>
    );
};

export default CsiProgrammesDataPage;